/**
 * Resolving a raycast hit to a business part.
 *
 * A hit always lands on a mesh, and a mesh is usually a technical child of the node that carries
 * the business identity (one primitive of a part, a sub-solid, a helper group...). Selection must
 * therefore walk up the ancestry until it finds a node the manifest knows about.
 *
 * The walk is expressed over a minimal shape rather than a 3D library's node type, so it can be
 * unit-tested with plain objects and reused whatever the renderer.
 */

/** The minimum a node must expose to be resolvable. */
export interface CadNodeLike {
  name: string;
  parent?: CadNodeLike | null | undefined;
}

/** Safety valve: assembly trees are shallow, a deeper walk means a cycle. */
const MAX_ANCESTRY_DEPTH = 512;

/**
 * Walks up from `node` and returns the id of the first ancestor (including `node` itself) whose
 * name maps to a business part. Returns `undefined` when the hit belongs to no known part, which
 * the caller should treat as a click on nothing.
 */
export function resolvePartIdFromNode(
  node: CadNodeLike | undefined | null,
  nodeNameToPartId: ReadonlyMap<string, string>
): string | undefined {
  let current: CadNodeLike | undefined | null = node;
  let depth = 0;
  while (current !== undefined && current !== null && depth < MAX_ANCESTRY_DEPTH) {
    const partId = nodeNameToPartId.get(current.name);
    if (partId !== undefined) {
      return partId;
    }
    current = current.parent;
    depth++;
  }
  return undefined;
}

/**
 * Walks up from `node` and returns the ancestor itself rather than its id. Used by the viewer to
 * find the object whose transform the exploded view animates.
 */
export function resolvePartNodeFrom<T extends CadNodeLike>(
  node: T | undefined | null,
  isPartNode: (candidate: T) => boolean
): T | undefined {
  let current: T | undefined | null = node;
  let depth = 0;
  while (current !== undefined && current !== null && depth < MAX_ANCESTRY_DEPTH) {
    if (isPartNode(current)) {
      return current;
    }
    current = current.parent as T | undefined | null;
    depth++;
  }
  return undefined;
}
